/**
 * Plain-words formatting shared by the laptop fleet screens (release 2.1
 * items 14-18). Pure, so it is unit-tested without a browser.
 */

import type { CoverageState, FleetLaptopCounts, Freshness, LivenessState } from '@/lib/fleet-api';

const INDIAN = new Intl.NumberFormat('en-IN');

export function fmtCount(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? INDIAN.format(n) : '-';
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${fmtCount(n)} ${n === 1 ? one : many}`;
}

/** "just now", "40 s ago", "5 min ago", "3 h ago", "2 days ago"; "never" for null. */
export function ago(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'never';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** "45 s", "3 min", "2 h": a duration, for lag. */
export function duration(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  return `${Math.round(m / 60)} h`;
}

/**
 * How current the rollup numbers are, in one sentence. Says so when they lag,
 * and never claims "current" to a reader who cannot see the queue.
 */
export function freshnessText(f: Freshness | null | undefined, now: number = Date.now()): string {
  if (!f) return '';
  if (!f.lastRecomputeAt) return 'No laptop results counted yet.';
  const counted = `Counted ${ago(f.countedAt ?? f.asOf, now)}.`;
  if (!f.visible) return `${counted} Counts cover the business units you can see.`;
  const oversize =
    (f.oversizeDevices ?? 0) > 0
      ? ` ${plural(f.oversizeDevices ?? 0, 'laptop name')} ${(f.oversizeDevices ?? 0) === 1 ? 'holds' : 'hold'} more than ${(
          f.oversizeLimit ?? 0
        ).toLocaleString('en-US')} findings and ${(f.oversizeDevices ?? 0) === 1 ? 'is' : 'are'} left out of these counts; several laptops may be reporting under one name.`
      : '';
  if (f.pendingDevices === 0) return `${counted} Every laptop result received by then is included.${oversize}`;
  return `${counted} ${plural(f.pendingDevices, 'laptop')} waiting to be recounted; numbers may be up to ${duration(f.lagSeconds)} behind.${oversize}`;
}

/*
 * Release 3.1: the words follow the one fleet vocabulary (the same as Edge
 * Fleet, lib/edge-health.ts). The API keeps its enum values - `reporting`,
 * `stale`, `never_reported` - and only the words shown change:
 *   reporting -> "Checking in"   stale -> "Down"   never_reported -> "Never checked in"
 */
export const COVERAGE_LABEL: Record<CoverageState, string> = {
  scanned: 'Scanned',
  scanned_with_skips: 'Scanned, some files not read',
  failed: 'Scan failed',
  paused: 'Paused',
  not_attempted: 'Online, not scanned',
  silent: 'Offline (1-7 days)',
  stale: 'Inactive (over 7 days)',
  never_reported: 'Pending',
};

/** Semantic colour per coverage state; literal class names so Tailwind keeps them. */
export const COVERAGE_BAR_CLASS: Record<CoverageState, string> = {
  scanned: 'bg-success',
  scanned_with_skips: 'bg-warning',
  failed: 'bg-destructive',
  paused: 'bg-info',
  not_attempted: 'bg-chart-4',
  silent: 'bg-chart-7',
  stale: 'bg-muted-foreground',
  never_reported: 'bg-border',
};

export const COVERAGE_ORDER: CoverageState[] = [
  'scanned',
  'scanned_with_skips',
  'failed',
  'paused',
  'not_attempted',
  'silent',
  'stale',
  'never_reported',
];

export const LIVENESS_LABEL: Record<LivenessState, string> = {
  reporting: 'Online',
  silent: 'Offline',
  stale: 'Inactive',
  never_reported: 'Pending',
};

/**
 * The laptop state tiles, in the one vocabulary. Reads the release 3.1 fields
 * and falls back to the older names for a controller that does not send them:
 * then Healthy counts every laptop checking in (the old `reporting`, healthy
 * and degraded together) and Degraded is null, because it cannot be split out.
 */
export function laptopStateCounts(l: FleetLaptopCounts): {
  healthy: number;
  degraded: number | null;
  silent: number;
  down: number;
  pending: number;
  /** False when the controller predates 3.1 and degraded is inside healthy. */
  split: boolean;
} {
  const split = typeof l.healthy === 'number' && typeof l.degraded === 'number';
  return {
    healthy: split ? (l.healthy as number) : l.reporting,
    degraded: split ? (l.degraded as number) : null,
    silent: l.silent,
    down: typeof l.down === 'number' ? l.down : l.stale,
    pending: typeof l.pending === 'number' ? l.pending : l.neverReported,
    split,
  };
}

/** Segments of a stacked bar, as percentages that add up to 100 (or all zero). */
export function barSegments<K extends string>(
  counts: Record<K, number>,
  order: K[],
): Array<{ key: K; count: number; pct: number }> {
  const total = order.reduce((s, k) => s + (counts[k] ?? 0), 0);
  return order.map((key) => ({
    key,
    count: counts[key] ?? 0,
    pct: total > 0 ? ((counts[key] ?? 0) / total) * 100 : 0,
  }));
}

export const SEVERITY_TEXT: Record<string, string> = {
  critical: 'text-sev-critical',
  high: 'text-sev-high',
  medium: 'text-sev-medium',
  low: 'text-sev-low',
  info: 'text-muted-foreground',
};

export const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-sev-critical',
  high: 'bg-sev-high',
  medium: 'bg-sev-medium',
  low: 'bg-sev-low',
  info: 'bg-muted-foreground',
};

export const SEVERITY_BY_RANK = ['info', 'low', 'medium', 'high', 'critical'] as const;

export function severityOfRank(rank: number | null | undefined): string | null {
  if (rank === null || rank === undefined) return null;
  return SEVERITY_BY_RANK[rank] ?? null;
}

/** "3 laptops share this label" when the label is not unique; '' otherwise. */
export function sharedLabelNote(pods: number | undefined): string {
  if (!pods || pods <= 1) return '';
  return `${pods} laptops use this name, so their files are counted together`;
}
